import axios from './axios'
import store from 'shared/redux/store'
import { showSuccess, showError } from 'shared/redux/app/reducer'
import { setIsUploading } from 'shared/redux/app/reducer'
import { createSelector } from 'reselect'
import { GlobalState } from 'shared/redux/reducer'
import { AxiosRequestConfig } from 'axios'
import { clearIndexedDB, base64ToBuffer, getMimeTypeFromFileName, randomNumber } from 'shared/utils/tools'
import getUploadLinkService from 'shared/utils/getUploadLink'
import getUploadLinkBatchService from 'shared/utils/getUploadLinkBatch'
import { UploadResponse } from 'pages/Store/model/product'
import axiosInstance from 'axios'
import JSZip from 'jszip'

type UploadData = {
  entity: string
  entity_id: string
  property: string
  is_content_metadata?: boolean
  organization_id?: any
  token?: string | null
  token_type?: string | null
}

export const isUploadingSelector = createSelector(
  (state: GlobalState) => state.app.isUploading,
  isUploading => isUploading,
)

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export default async function uploadService(
  base64string: string,
  params: UploadData,
  filename: string,
  fileType = 'image/png',
  isAwsPresigned = false,
  config?: AxiosRequestConfig,
) {
  try {
    const mime = base64string.match(/data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+).*,.*/)
    let mimeType = fileType
    if (mime && mime.length) {
      mimeType = mime[1]
    }

    if (isAwsPresigned && !config?.url) {
      const formData = new FormData()
      Object.entries(params).forEach(([key, val]) => formData.append(key, val))
      store.dispatch(setIsUploading(true))
      const fileBuffer = base64ToBuffer(base64string)

      if (fileType === 'application/zip') {
        const zipFile = await fetch(base64string)
          .then(res => res.arrayBuffer())
          .then(buffer => new File([buffer], filename, { type: mimeType }))

        const jszip = new JSZip()
        const zip = await jszip.loadAsync(zipFile)

        const path = `${params.entity}/${params.property}/${params.entity_id}/${filename}`
        console.log('Upload path:', path)
        console.log('Upload params:', params)

        const filesToUpload: {
          name: string
          mimeType: string
          buffer: Buffer
        }[] = []

        for (const fileNameInZip in zip.files) {
          const zipEntry = zip.files[fileNameInZip]
          if (!zipEntry.dir) {
            const mimeType = getMimeTypeFromFileName(fileNameInZip)
            const arrayBuffer = await zipEntry.async('arraybuffer')

            filesToUpload.push({
              name: fileNameInZip,
              mimeType,
              buffer: Buffer.from(arrayBuffer),
            })
          }
        }

        const presignedUrls = await getUploadLinkBatchService(
          {},
          path,
          filesToUpload.map(f => ({
            filename: f.name,
            file_type: f.mimeType,
            file_sub_path: f.name,
          })),
        )

        const uploadUrlMap = new Map<string, string>(presignedUrls.map(p => [p.filename, String(p.file_path)]))

        const CONCURRENCY = 3

        for (let i = 0; i < filesToUpload.length; i += CONCURRENCY) {
          const chunk = filesToUpload.slice(i, i + CONCURRENCY)

          await Promise.all(
            chunk.map(file => {
              const uploadUrl = uploadUrlMap.get(file.name) as string

              return axiosInstance({
                url: uploadUrl,
                method: 'PUT',
                ...(config || {}),
                headers: {
                  'Content-Type': file.mimeType,
                  'Content-Length': file.buffer.length,
                  'Content-Disposition': 'inline',
                },
                data: file.buffer,
              })
            }),
          )

          // Optional micro-delay for extra WAF safety
          await sleep(randomNumber(500, 2000))
        }

        // 6️⃣ Final asset update
        const contentUrl = `${path}/content/index.html`
        formData.append('value', contentUrl)
        return axios({
          url: '/asset',
          method: 'PUT',
          ...(config || {}),
          data: formData,
        })
          .then(response => response)
          .finally(() => {
            store.dispatch(setIsUploading(false))
          })
      } else {
        const path = `${params.entity}/${params.property}/${params.entity_id}/${filename}`
        const uploadLinkResponse = await getUploadLinkService(
          {},
          path,
          mimeType,
          '',
          params.token || '',
          params.token_type || '',
        )
        if (!uploadLinkResponse) {
          store.dispatch(setIsUploading(false))
          throw new Error('Unable to get upload link.')
        }
        return axiosInstance({
          url: uploadLinkResponse.file_path,
          method: 'PUT',
          ...(config || {}),
          headers: {
            'Content-Type': mimeType,
            'Content-Length': fileBuffer.length,
            'Content-Disposition': 'inline',
          },
          data: fileBuffer,
        })
          .then(async response => {
            formData.append('value', path)
            return axios({
              url: `/asset${params.token && params.token_type ? '/public' : ''}`,
              method: 'PUT',
              ...(config || {}),
              data: formData,
            })
          })
          .then(secondResponse => secondResponse)
          .finally(() => {
            store.dispatch(setIsUploading(false))
          })
          .catch(error => {
            store.dispatch(showError({ message: 'Error during file upload.' }))
            throw error
          })
      }
    } else {
      const formData = new FormData()
      Object.entries(params).forEach(([key, val]) => formData.append(key, val))
      const file = await fetch(base64string)
        .then(res => res.arrayBuffer())
        .then(buffer => new File([buffer], filename, { type: mimeType }))
      formData.append('file', file)
      if (fileType == 'application/zip' || fileType == 'application/pdf') {
        formData.append('file_type', fileType)
      } else if (fileType == 'image/png') {
        if (process.env.REACT_APP_ASSET_STORAGE != 's3') {
          clearIndexedDB('images')
        }
      }

      store.dispatch(setIsUploading(true))
      return axios({
        url: fileType == 'application/zip' ? '/file/upload/zip' : '/file/upload',
        method: 'POST',
        ...(config || {}),
        data: formData,
      })
        .then(response => {
          store.dispatch(setIsUploading(false))
          return response
        })
        .finally(() => {
          store.dispatch(setIsUploading(false))
        })
    }
  } catch (error) {
    console.error('uploadService error:', error)
    console.error('Error response:', error.response?.data)
    console.error('Error status:', error.response?.status)
    store.dispatch(
      showError({
        message: error.response?.data?.message || error.message || 'Error during file upload',
      }),
    )
    store.dispatch(setIsUploading(false))
    throw error
  }
}
